import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Icon,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  MdAgriculture,
  MdArrowForward,
  MdAssignment,
  MdCheckCircle,
  MdInventory2,
  MdLocationOn,
  MdPeople,
  MdStorefront,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AppCard, DashboardLoader } from '../../../components/ui';
import { useAdminHeaderRefresh } from '../../../contexts/AdminHeaderRefreshContext';
import { getDashboardCounts } from '../../../repositories/dashboard.repository';

/** Chart palette — hex values because Recharts renders raw SVG. */
const CHART = {
  amber: '#F9B101',
  amberSoft: '#FBD66A',
  success: '#2E7D32',
  successSoft: '#66BB6A',
  info: '#1B4D3E',
  infoSoft: '#3E7F6B',
  warning: '#B86E00',
  danger: '#C62828',
  muted: '#9C9992',
};

/** Shared plot height so the pipeline and equipment panels line up. */
const CHART_H = 196;

function pct(part, whole) {
  const p = Number(part) || 0;
  const w = Number(whole) || 0;
  if (w <= 0) return 0;
  return Math.round((p / w) * 100);
}

function ChartTooltip({ active, payload, label, total }) {
  const bg = useColorModeValue('#FFFFFF', '#242220');
  const border = useColorModeValue('#E2DED2', '#3D3A35');
  const text = useColorModeValue('#141414', '#FAF9F6');
  const sub = useColorModeValue('#5C574C', '#A8A49A');

  if (!active || !payload?.length) return null;
  const row = payload[0];
  const name = row?.payload?.name || label;
  const value = Number(row?.value) || 0;

  return (
    <Box
      bg={bg}
      borderWidth="1px"
      borderColor={border}
      borderRadius="md"
      px={3}
      py={2}
      boxShadow="lg"
      minW="140px"
    >
      <Text fontSize="12px" color={sub} mb={0.5}>
        {name}
      </Text>
      <Text fontSize="16px" fontWeight="700" color={text} fontVariantNumeric="tabular-nums">
        {value}
        {total ? (
          <Text as="span" fontSize="12px" fontWeight="500" color={sub} ml={1.5}>
            {pct(value, total)}% of {total}
          </Text>
        ) : null}
      </Text>
    </Box>
  );
}

function PanelHeader({ title, subtitle, right }) {
  return (
    <Flex justify="space-between" align="flex-start" gap={3} mb={4}>
      <Box minW={0}>
        <Text fontWeight="700" fontSize="15px" color="text.primary">
          {title}
        </Text>
        {subtitle ? (
          <Text fontSize="13px" color="text.secondary" mt={0.5}>
            {subtitle}
          </Text>
        ) : null}
      </Box>
      {right}
    </Flex>
  );
}

function EmptyChart({ label, h = 240 }) {
  return (
    <Flex
      align="center"
      justify="center"
      h={`${h}px`}
      borderRadius="md"
      bg="bg.input"
      borderWidth="1px"
      borderStyle="dashed"
      borderColor="border.subtle"
    >
      <Text fontSize="13px" color="text.secondary">
        {label}
      </Text>
    </Flex>
  );
}

function KpiTile({ icon, label, value, hint, share, shareLabel, color, onClick }) {
  const trackColor = useColorModeValue('#F3F1EC', '#2A2825');
  const data = [
    { name: shareLabel, value: Math.max(0, Number(share) || 0) },
    { name: 'Rest', value: Math.max(0, 100 - (Number(share) || 0)) },
  ];

  return (
    <AppCard
      p={4}
      h="100%"
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
      transition="border-color 0.15s ease"
      _hover={onClick ? { borderColor: 'utkarsh.amber' } : undefined}
    >
      <Flex align="center" justify="space-between" gap={3}>
        <Box minW={0}>
          <Flex align="center" gap={2} mb={2}>
            <Icon as={icon} boxSize="16px" color="text.secondary" />
            <Text
              fontSize="11px"
              fontWeight="700"
              letterSpacing="0.06em"
              textTransform="uppercase"
              color="text.secondary"
              noOfLines={1}
            >
              {label}
            </Text>
          </Flex>
          <Text
            fontSize="30px"
            fontWeight="700"
            lineHeight="1.05"
            fontVariantNumeric="tabular-nums"
          >
            {value ?? 0}
          </Text>
          <Text fontSize="12px" color="text.secondary" mt={1.5} noOfLines={2}>
            {hint}
          </Text>
        </Box>

        <Box position="relative" w="68px" h="68px" flexShrink={0}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                innerRadius={24}
                outerRadius={33}
                startAngle={90}
                endAngle={-270}
                stroke="none"
                isAnimationActive={false}
              >
                <Cell fill={color} />
                <Cell fill={trackColor} />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <Flex
            position="absolute"
            inset={0}
            align="center"
            justify="center"
            pointerEvents="none"
          >
            <Text fontSize="12px" fontWeight="700" fontVariantNumeric="tabular-nums">
              {Math.max(0, Number(share) || 0)}%
            </Text>
          </Flex>
        </Box>
      </Flex>
    </AppCard>
  );
}

export default function Dashboard() {
  const toast = useToast();
  const navigate = useNavigate();
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);

  const axisColor = useColorModeValue('#5C574C', '#A8A49A');
  const gridStroke = useColorModeValue('#E2DED2', '#3D3A35');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDashboardCounts();
      setCounts(data);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load dashboard',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  useAdminHeaderRefresh(load);

  const villages = counts?.villages || {};
  const vd = counts?.vendors || {};
  const materials = counts?.materials || {};
  const specialServices = counts?.specialServices || {};
  const eq = counts?.equipment || {};
  const ind = counts?.indents || {};
  const lineTypes = ind.lineTypes || {};

  const pipelineData = useMemo(() => {
    const i = counts?.indents || {};
    return [
      { name: 'Pending', value: Number(i.pending) || 0, fill: CHART.warning },
      { name: 'Recommended', value: Number(i.recommended) || 0, fill: CHART.info },
      { name: 'Assigned', value: Number(i.assigned) || 0, fill: CHART.amber },
      { name: 'Released', value: Number(i.released) || 0, fill: CHART.success },
      { name: 'Cancelled', value: Number(i.cancelled) || 0, fill: CHART.muted },
    ];
  }, [counts]);
  const pipelineTotal = pipelineData.reduce((s, r) => s + r.value, 0);
  const pipelineMax = Math.max(1, ...pipelineData.map((r) => r.value));

  const fleetData = useMemo(() => {
    const e = counts?.equipment || {};
    return [
      { name: 'Available', value: Number(e.available) || 0, fill: CHART.success },
      { name: 'Deployed', value: Number(e.deployed) || 0, fill: CHART.amber },
      { name: 'Pending KYC', value: Number(e.pendingKyc) || 0, fill: CHART.warning },
    ];
  }, [counts]);
  const fleetTotal = fleetData.reduce((s, r) => s + r.value, 0);

  const specificationData = useMemo(() => {
    const s = counts?.vendors?.specifications || {};
    return [
      { name: 'Agency', value: Number(s.agency) || 0, fill: CHART.amber },
      { name: 'Equipment', value: Number(s.equipment) || 0, fill: CHART.success },
      { name: 'Material', value: Number(s.materialSupply) || 0, fill: CHART.infoSoft },
      {
        name: 'Special services',
        value: Number(s.specialServices) || 0,
        fill: CHART.info,
      },
    ];
  }, [counts]);
  const specificationTotal = specificationData.reduce((s, r) => s + r.value, 0);

  const demandByTypeData = useMemo(() => {
    const lt = counts?.indents?.lineTypes || {};
    return [
      { name: 'Equipment', value: Number(lt.equipment) || 0, fill: CHART.success },
      { name: 'Material', value: Number(lt.material) || 0, fill: CHART.amber },
      {
        name: 'Special services',
        value: Number(lt.specialService) || 0,
        fill: CHART.info,
      },
    ];
  }, [counts]);
  const demandByTypeTotal = demandByTypeData.reduce((s, r) => s + r.value, 0);

  const specialTagData = useMemo(() => {
    const tags = counts?.specialServices?.tags || {};
    return Object.entries(tags)
      .map(([name, value]) => ({
        name,
        value: Number(value) || 0,
        fill: CHART.infoSoft,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [counts]);

  const attention = useMemo(() => {
    const i = counts?.indents || {};
    const e = counts?.equipment || {};
    const m = counts?.materials || {};
    const vn = counts?.vendors || {};
    return [
      {
        key: 'indent-pending',
        label: 'Open requests',
        value: Number(i.pending) || 0,
        color: CHART.warning,
        to: '/admin/equipments/indents',
      },
      {
        key: 'eq-kyc',
        label: 'Equipment KYC pending',
        value: Number(e.pendingKyc) || 0,
        color: CHART.amber,
        to: '/admin/equipments/fleet',
      },
      {
        key: 'material-pending',
        label: 'Material listings pending',
        value: Number(m.pending) || 0,
        color: CHART.info,
        to: '/admin/users',
      },
      {
        key: 'member-pending',
        label: 'Agency / provider reviews pending',
        value: Number(vn.pending) || 0,
        color: CHART.danger,
        to: '/admin/users',
      },
    ].filter((row) => row.value > 0);
  }, [counts]);

  if (loading) {
    return <DashboardLoader cards={4} />;
  }

  return (
    <Box w="100%" maxW="100%">
      <Box mb={{ base: 4, md: 5 }}>
        <Heading size={{ base: 'md', md: 'lg' }} letterSpacing="-0.02em">
          Dashboard
        </Heading>
        <Text color="text.secondary" mt={1} fontSize={{ base: '13px', md: '14px' }}>
          Operations snapshot for your scope — requests, villages, fleet, and member specifications.
        </Text>
      </Box>

      {/* Zone 1 — primary KPIs with share rings */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3} mb={3}>
        <KpiTile
          icon={MdAssignment}
          label="Requests"
          value={ind.total ?? 0}
          hint={`${ind.pending ?? 0} pending · ${ind.assigned ?? 0} live · ${ind.released ?? 0} released`}
          share={pct((ind.assigned || 0) + (ind.released || 0), ind.total)}
          shareLabel="Actioned"
          color={CHART.amber}
          onClick={() => navigate('/admin/equipments/indents')}
        />
        <KpiTile
          icon={MdAgriculture}
          label="Equipment"
          value={eq.total ?? 0}
          hint={`${eq.available ?? 0} available · ${eq.deployed ?? 0} deployed`}
          share={pct(eq.available, eq.total)}
          shareLabel="Available"
          color={CHART.success}
          onClick={() => navigate('/admin/equipments/fleet')}
        />
        <KpiTile
          icon={MdLocationOn}
          label="Villages"
          value={villages.total ?? 0}
          hint={`${villages.active ?? 0} active in master data`}
          share={pct(villages.active, villages.total)}
          shareLabel="Active"
          color={CHART.info}
          onClick={() => navigate('/admin/master-data/villages')}
        />
        <KpiTile
          icon={MdStorefront}
          label="Agencies & providers"
          value={vd.total ?? 0}
          hint={`${vd.agencies?.total ?? 0} agencies · ${vd.serviceProviders?.total ?? 0} providers · ${vd.verified ?? 0} verified`}
          share={pct(vd.verified, vd.total)}
          shareLabel="Verified"
          color={CHART.amberSoft}
          onClick={() => navigate('/admin/users')}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} mb={3}>
        <KpiTile
          icon={MdInventory2}
          label="Material listings"
          value={materials.total ?? 0}
          hint={`${materials.verified ?? 0} verified · ${materials.pending ?? 0} pending · ${materials.rejected ?? 0} rejected`}
          share={pct(materials.verified, materials.total)}
          shareLabel="Verified"
          color={CHART.infoSoft}
          onClick={() => navigate('/admin/users')}
        />
        <KpiTile
          icon={MdPeople}
          label="Special services"
          value={specialServices.members ?? 0}
          hint={`providers offering it · ${Object.keys(specialServices.tags || {}).length} tags · ${lineTypes.specialService ?? 0} request lines`}
          share={pct(specialServices.members, vd.serviceProviders?.total || vd.total)}
          shareLabel="Of providers"
          color={CHART.info}
          onClick={() => navigate('/admin/users')}
        />
      </SimpleGrid>

      {/* Zone 2 — actionable queue */}
      {attention.length ? (
        <AppCard p={0} overflow="hidden" mb={3}>
          <Flex
            px={4}
            py={2.5}
            borderBottomWidth="1px"
            borderColor="border.subtle"
            bg="bg.input"
            align="center"
            justify="space-between"
          >
            <Text
              fontSize="11px"
              fontWeight="700"
              letterSpacing="0.06em"
              textTransform="uppercase"
              color="text.secondary"
            >
              Needs attention
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: attention.length }} spacing={0}>
            {attention.map((item, idx) => (
              <Flex
                key={item.key}
                as="button"
                type="button"
                onClick={() => navigate(item.to)}
                textAlign="left"
                px={4}
                py={3.5}
                align="center"
                gap={3}
                borderRightWidth={{ lg: idx < attention.length - 1 ? '1px' : 0 }}
                borderBottomWidth={{ base: '1px', lg: 0 }}
                borderColor="border.subtle"
                _hover={{ bg: 'bg.input' }}
                sx={{ '& > *': { pointerEvents: 'none' } }}
              >
                <Box w="8px" h="8px" borderRadius="full" bg={item.color} flexShrink={0} />
                <Box flex="1" minW={0}>
                  <Text fontSize="12px" color="text.secondary" noOfLines={1}>
                    {item.label}
                  </Text>
                  <Text
                    fontSize="20px"
                    fontWeight="700"
                    lineHeight="1.15"
                    fontVariantNumeric="tabular-nums"
                  >
                    {item.value}
                  </Text>
                </Box>
                <Icon as={MdArrowForward} boxSize="16px" color="text.secondary" />
              </Flex>
            ))}
          </SimpleGrid>
        </AppCard>
      ) : (
        <AppCard p={4} mb={3}>
          <Flex align="center" gap={3}>
            <Icon as={MdCheckCircle} boxSize={5} color="status.success" />
            <Box>
              <Text fontWeight="600" fontSize="14px">
                Operations look clear
              </Text>
              <Text fontSize="13px" color="text.secondary">
                No pending KYC, agency/provider reviews, or open requests in your scope.
              </Text>
            </Box>
          </Flex>
        </AppCard>
      )}

      {/* Zone 3 — charts */}
      <Grid templateColumns={{ base: '1fr', lg: '1.25fr 1fr' }} gap={3} mb={3}>
        <GridItem>
          <AppCard p={{ base: 4, xl: 5 }} h="100%">
            <PanelHeader
              title="Request pipeline"
              subtitle="Where indents sit today, from raise to release"
              right={
                <Box textAlign="right" flexShrink={0}>
                  <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color="text.secondary">
                    Total
                  </Text>
                  <Text fontSize="20px" fontWeight="700" fontVariantNumeric="tabular-nums" lineHeight="1.2">
                    {pipelineTotal}
                  </Text>
                </Box>
              }
            />
            {pipelineTotal === 0 ? (
              <EmptyChart label="No requests in scope yet" h={CHART_H} />
            ) : (
              <Box h={`${CHART_H}px`}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={pipelineData}
                    layout="vertical"
                    margin={{ top: 0, right: 36, bottom: 0, left: 0 }}
                    barCategoryGap="14%"
                  >
                    <XAxis type="number" domain={[0, pipelineMax]} hide />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={96}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: axisColor, fontSize: 12 }}
                    />
                    <Tooltip
                      cursor={{ fill: gridStroke, opacity: 0.25 }}
                      content={<ChartTooltip total={pipelineTotal} />}
                    />
                    <Bar
                      dataKey="value"
                      radius={[0, 6, 6, 0]}
                      maxBarSize={30}
                      background={{ fill: gridStroke, opacity: 0.28, radius: 6 }}
                    >
                      {pipelineData.map((row) => (
                        <Cell key={row.name} fill={row.fill} />
                      ))}
                      <LabelList
                        dataKey="value"
                        position="right"
                        style={{ fill: axisColor, fontSize: 12, fontWeight: 700 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </AppCard>
        </GridItem>

        <GridItem>
          <AppCard p={{ base: 4, xl: 5 }} h="100%">
            <PanelHeader
              title="Equipment readiness"
              subtitle="Fleet assets available vs on work vs KYC hold"
            />
            {fleetTotal === 0 ? (
              <EmptyChart label="No equipment in scope yet" h={CHART_H} />
            ) : (
              <Flex
                minH={`${CHART_H}px`}
                align="center"
                gap={{ base: 4, md: 5 }}
                direction={{ base: 'column', sm: 'row' }}
              >
                <Box
                  position="relative"
                  w={{ base: '160px', sm: `${CHART_H}px` }}
                  h={{ base: '160px', sm: `${CHART_H}px` }}
                  flexShrink={0}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                      <Pie
                        data={fleetData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius="64%"
                        outerRadius="94%"
                        paddingAngle={2}
                        stroke="none"
                      >
                        {fleetData.map((row) => (
                          <Cell key={row.name} fill={row.fill} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip total={fleetTotal} />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Flex
                    position="absolute"
                    inset={0}
                    align="center"
                    justify="center"
                    direction="column"
                    pointerEvents="none"
                  >
                    <Text
                      fontSize="28px"
                      fontWeight="700"
                      lineHeight="1"
                      fontVariantNumeric="tabular-nums"
                    >
                      {fleetTotal}
                    </Text>
                    <Text
                      fontSize="10px"
                      color="text.secondary"
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                      mt={1}
                    >
                      Assets
                    </Text>
                  </Flex>
                </Box>

                <Box flex="1" minW={0}>
                  {fleetData.map((row, idx) => (
                    <Flex
                      key={row.name}
                      align="center"
                      gap={3}
                      py={2.5}
                      borderTopWidth={idx === 0 ? 0 : '1px'}
                      borderColor="border.subtle"
                    >
                      <Box
                        w="10px"
                        h="10px"
                        borderRadius="sm"
                        bg={row.fill}
                        flexShrink={0}
                      />
                      <Text flex="1" fontSize="13px" noOfLines={1}>
                        {row.name}
                      </Text>
                      <Text
                        fontSize="15px"
                        fontWeight="700"
                        fontVariantNumeric="tabular-nums"
                      >
                        {row.value}
                      </Text>
                      <Text
                        fontSize="12px"
                        color="text.secondary"
                        fontVariantNumeric="tabular-nums"
                        minW="38px"
                        textAlign="right"
                      >
                        {pct(row.value, fleetTotal)}%
                      </Text>
                    </Flex>
                  ))}
                </Box>
              </Flex>
            )}
          </AppCard>
        </GridItem>
      </Grid>

      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={3} mb={3}>
        <GridItem>
          <AppCard p={{ base: 4, xl: 5 }} h="100%">
            <PanelHeader
              title="Member specifications"
              subtitle="How members are set up — agency vs equipment, material, and special services"
              right={
                <Box textAlign="right" flexShrink={0}>
                  <Text
                    fontSize="11px"
                    fontWeight="700"
                    textTransform="uppercase"
                    color="text.secondary"
                  >
                    Selections
                  </Text>
                  <Text
                    fontSize="20px"
                    fontWeight="700"
                    fontVariantNumeric="tabular-nums"
                    lineHeight="1.2"
                  >
                    {specificationTotal}
                  </Text>
                </Box>
              }
            />
            {specificationTotal === 0 ? (
              <EmptyChart label="No member specifications in scope yet" h={200} />
            ) : (
              <Box h={{ base: '220px', md: '240px' }} w="100%">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={specificationData}
                    margin={{ top: 8, right: 12, bottom: 4, left: 0 }}
                    barCategoryGap="18%"
                  >
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: axisColor, fontSize: 11, fontWeight: 600 }}
                      interval={0}
                    />
                    <YAxis
                      allowDecimals={false}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: axisColor, fontSize: 12 }}
                      width={32}
                    />
                    <Tooltip
                      content={<ChartTooltip total={specificationTotal} />}
                      cursor={{ fill: gridStroke, opacity: 0.25 }}
                    />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={72}>
                      {specificationData.map((row) => (
                        <Cell key={row.name} fill={row.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </AppCard>
        </GridItem>
        <GridItem>
          <AppCard p={{ base: 4, xl: 5 }} h="100%">
            <PanelHeader
              title="Request demand by type"
              subtitle="Indent lines across equipment, material, and special services"
              right={
                <Box textAlign="right" flexShrink={0}>
                  <Text
                    fontSize="11px"
                    fontWeight="700"
                    textTransform="uppercase"
                    color="text.secondary"
                  >
                    Lines
                  </Text>
                  <Text
                    fontSize="20px"
                    fontWeight="700"
                    fontVariantNumeric="tabular-nums"
                    lineHeight="1.2"
                  >
                    {demandByTypeTotal}
                  </Text>
                </Box>
              }
            />
            {demandByTypeTotal === 0 ? (
              <EmptyChart label="No request lines in scope yet" h={200} />
            ) : (
              <Flex direction={{ base: 'column', md: 'row' }} align="center" gap={4}>
                <Box w={{ base: '100%', md: '46%' }} h={CHART_H}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={demandByTypeData.filter((r) => r.value > 0)}
                        dataKey="value"
                        nameKey="name"
                        innerRadius="58%"
                        outerRadius="82%"
                        paddingAngle={2}
                      >
                        {demandByTypeData
                          .filter((r) => r.value > 0)
                          .map((row) => (
                            <Cell key={row.name} fill={row.fill} />
                          ))}
                        <LabelList
                          dataKey="value"
                          position="outside"
                          fill={axisColor}
                          fontSize={11}
                        />
                      </Pie>
                      <Tooltip content={<ChartTooltip total={demandByTypeTotal} />} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <Box flex="1" w="100%">
                  {demandByTypeData.map((row) => (
                    <Flex key={row.name} align="center" gap={2} py={1.5}>
                      <Box w="8px" h="8px" borderRadius="full" bg={row.fill} flexShrink={0} />
                      <Text fontSize="13px" flex="1" noOfLines={1}>
                        {row.name}
                      </Text>
                      <Text fontSize="14px" fontWeight="700" fontVariantNumeric="tabular-nums">
                        {row.value}
                      </Text>
                      <Text
                        fontSize="12px"
                        color="text.secondary"
                        fontVariantNumeric="tabular-nums"
                        minW="38px"
                        textAlign="right"
                      >
                        {pct(row.value, demandByTypeTotal)}%
                      </Text>
                    </Flex>
                  ))}
                </Box>
              </Flex>
            )}
          </AppCard>
        </GridItem>
      </Grid>

      <AppCard p={{ base: 4, xl: 5 }}>
        <PanelHeader
          title="Special service types"
          subtitle="Tags selected by providers who offer special services"
          right={
            <Box textAlign="right" flexShrink={0}>
              <Text
                fontSize="11px"
                fontWeight="700"
                textTransform="uppercase"
                color="text.secondary"
              >
                Providers
              </Text>
              <Text
                fontSize="20px"
                fontWeight="700"
                fontVariantNumeric="tabular-nums"
                lineHeight="1.2"
              >
                {specialServices.members ?? 0}
              </Text>
            </Box>
          }
        />
        {specialTagData.length === 0 ? (
          <EmptyChart label="No special service tags in scope yet" h={180} />
        ) : (
          <Box h={{ base: '200px', md: '220px' }} w="100%">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={specialTagData}
                layout="vertical"
                margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
              >
                <XAxis type="number" allowDecimals={false} hide />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={120}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: axisColor, fontSize: 12, fontWeight: 600 }}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: gridStroke, opacity: 0.25 }} />
                <Bar dataKey="value" fill={CHART.info} radius={[0, 6, 6, 0]} maxBarSize={22}>
                  <LabelList
                    dataKey="value"
                    position="right"
                    fill={axisColor}
                    fontSize={12}
                    fontWeight={700}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </AppCard>
    </Box>
  );
}
